import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, Activity, Heart, Clock, FileText, Send, Eye, Stethoscope, ClipboardList, BookOpen } from 'lucide-react';

interface MobilityMetrics {
  gaitSpeed: number;
  stepSymmetry: number;
  stanceTime: number;
  turnDuration: number;
  balanceScore: number;
  dailySteps: number;
  timeOnFeet: number;
}

interface BathroomMetrics {
  frequency: number;
  nighttime: number;
  duration: number;
  irregularPattern: boolean;
}

interface PhysicianNotes {
  clinicalObservations: string;
  treatmentPlan: string;
  physicalTherapy: string;
  followUpNotes: string;
  lastUpdated?: Date;
}

const PhysicianReport: React.FC = () => {
  const [showPhysicianView, setShowPhysicianView] = useState(false);
  const [careTeamNotes, setCareTeamNotes] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [physicianNotes, setPhysicianNotes] = useState<PhysicianNotes>({
    clinicalObservations: '',
    treatmentPlan: '',
    physicalTherapy: '',
    followUpNotes: '',
    lastUpdated: undefined
  });

  // Mock data
  const mobilityMetrics: MobilityMetrics = {
    gaitSpeed: 2.6, // feet per second
    stepSymmetry: 85, // percentage
    stanceTime: 1.2, // seconds
    turnDuration: 2.5, // seconds
    balanceScore: 72, // percentage
    dailySteps: 2500,
    timeOnFeet: 180 // minutes
  };

  const bathroomMetrics: BathroomMetrics = {
    frequency: 8,
    nighttime: 3,
    duration: 4.5, // minutes average
    irregularPattern: true
  };

  const weeklyTrends = [
    { day: 'Mon', gaitSpeed: 2.8, balance: 75, steps: 2800 },
    { day: 'Tue', gaitSpeed: 2.7, balance: 73, steps: 2600 },
    { day: 'Wed', gaitSpeed: 2.6, balance: 70, steps: 2400 },
    { day: 'Thu', gaitSpeed: 2.5, balance: 68, steps: 2200 },
    { day: 'Fri', gaitSpeed: 2.6, balance: 72, steps: 2500 },
    { day: 'Sat', gaitSpeed: 2.5, balance: 71, steps: 2300 },
    { day: 'Sun', gaitSpeed: 2.6, balance: 72, steps: 2500 }
  ];

  const handlePhysicianNotesChange = (field: keyof PhysicianNotes, value: string) => {
    setPhysicianNotes(prev => ({
      ...prev,
      [field]: value,
      lastUpdated: new Date()
    }));
  };

  const handleSendReport = () => {
    setReportSent(true);
    setTimeout(() => {
      setReportSent(false);
    }, 3000);
  };

  const getRiskLevel = (metric: number, threshold: number) => {
    if (metric < threshold * 0.7) return 'HIGH';
    if (metric < threshold * 0.85) return 'MEDIUM';
    return 'LOW';
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const PhysicianNotesSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold flex items-center">
            <Stethoscope className="mr-2 text-blue-500" />
            Clinical Observations
          </h3>
          {physicianNotes.lastUpdated && (
            <span className="text-sm text-gray-500">
              Last updated: {physicianNotes.lastUpdated.toLocaleString()}
            </span>
          )}
        </div>
        <textarea
          value={physicianNotes.clinicalObservations}
          onChange={(e) => handlePhysicianNotesChange('clinicalObservations', e.target.value)}
          placeholder="Enter clinical observations based on the mobility data..."
          className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <ClipboardList className="mr-2 text-green-500" />
          Treatment Plan
        </h3>
        <textarea
          value={physicianNotes.treatmentPlan}
          onChange={(e) => handlePhysicianNotesChange('treatmentPlan', e.target.value)}
          placeholder="Outline treatment plan and interventions..."
          className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <Activity className="mr-2 text-purple-500" />
          Physical Therapy Recommendations
        </h3>
        <textarea
          value={physicianNotes.physicalTherapy}
          onChange={(e) => handlePhysicianNotesChange('physicalTherapy', e.target.value)}
          placeholder="Specify physical therapy exercises and goals..."
          className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <BookOpen className="mr-2 text-orange-500" />
          Follow-up Notes & Additional Observations
        </h3>
        <textarea
          value={physicianNotes.followUpNotes}
          onChange={(e) => handlePhysicianNotesChange('followUpNotes', e.target.value)}
          placeholder="Add follow-up notes and additional observations..."
          className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );

  const ReportContent = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Gait Analysis Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-2">Key Metrics</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Gait Speed</span>
                <span className={`font-medium ${getRiskLevel(mobilityMetrics.gaitSpeed, 1.2) === 'HIGH' ? 'text-red-500' : 'text-green-500'}`}>
                  {mobilityMetrics.gaitSpeed} ft/s
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Step Symmetry</span>
                <span className={`font-medium ${getRiskLevel(mobilityMetrics.stepSymmetry, 95) === 'HIGH' ? 'text-red-500' : 'text-green-500'}`}>
                  {mobilityMetrics.stepSymmetry}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Balance Score</span>
                <span className={`font-medium ${getRiskLevel(mobilityMetrics.balanceScore, 85) === 'HIGH' ? 'text-red-500' : 'text-green-500'}`}>
                  {mobilityMetrics.balanceScore}%
                </span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Activity Levels</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Daily Steps</span>
                <span className="font-medium">{mobilityMetrics.dailySteps}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Time on Feet</span>
                <span className="font-medium">{formatTime(mobilityMetrics.timeOnFeet)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Turn Duration</span>
                <span className={`font-medium ${mobilityMetrics.turnDuration > 2.0 ? 'text-red-500' : 'text-green-500'}`}>
                  {mobilityMetrics.turnDuration}s
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Bathroom Visit Analysis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-2">Visit Patterns</h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span>Daily Frequency</span>
                <span className={`font-medium ${bathroomMetrics.frequency > 7 ? 'text-yellow-500' : 'text-green-500'}`}>
                  {bathroomMetrics.frequency} visits
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Nighttime Visits</span>
                <span className={`font-medium ${bathroomMetrics.nighttime > 2 ? 'text-red-500' : 'text-green-500'}`}>
                  {bathroomMetrics.nighttime} visits
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Average Duration</span>
                <span className="font-medium">{bathroomMetrics.duration} minutes</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-medium mb-2">Risk Indicators</h4>
            <div className="space-y-2">
              {bathroomMetrics.irregularPattern && (
                <div className="flex items-start space-x-2 text-yellow-500">
                  <AlertTriangle size={20} />
                  <span>Irregular pattern detected - possible UTI risk</span>
                </div>
              )}
              {bathroomMetrics.nighttime > 2 && (
                <div className="flex items-start space-x-2 text-red-500">
                  <AlertTriangle size={20} />
                  <span>Increased nighttime activity - fall risk</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-xl font-semibold mb-4">Weekly Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={weeklyTrends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line 
              yAxisId="left"
              type="monotone" 
              dataKey="gaitSpeed" 
              stroke="#8884d8" 
              name="Gait Speed (ft/s)"
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="balance" 
              stroke="#82ca9d" 
              name="Balance Score"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {!showPhysicianView && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Care Team Notes</h3>
          <textarea
            value={careTeamNotes}
            onChange={(e) => setCareTeamNotes(e.target.value)}
            placeholder="Add notes for the physician..."
            className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {showPhysicianView && (
        <>
          {careTeamNotes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold mb-4">Care Team Notes</h3>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-700">{careTeamNotes}</p>
              </div>
            </div>
          )}
          <PhysicianNotesSection />
        </>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">
            {showPhysicianView ? 'Physician Assessment' : 'Care Team Report'}
          </h2>
          <p className="text-gray-600">Emma Martinez | Age: 78 | Room: 204</p>
        </div>
        <div className="flex space-x-4">
          {!showPhysicianView && (
            <button
              onClick={handleSendReport}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              disabled={reportSent}
            >
              <Send size={18} className="mr-2" />
              {reportSent ? 'Report Sent!' : 'Send to Physician'}
            </button>
          )}
          <button
            onClick={() => setShowPhysicianView(!showPhysicianView)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Eye size={18} className="mr-2" />
            {showPhysicianView ? 'Care Team View' : 'Physician View'}
          </button>
        </div>
      </div>

      <ReportContent />
    </div>
  );
};

export default PhysicianReport; 